import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Building2, 
  User, 
  ArrowLeft, 
  ArrowRight,
  ShieldCheck,
  CheckCircle,
  Lock,
  Check,
  Loader2,
  FileText,
  CreditCard,
  Camera,
  MapPin,
  Mail,
  AlertCircle,
  Globe,
  UserPlus,
  Zap,
  Clock,
  Download,
  PhoneCall,
  Key,
  HelpCircle,
  ChevronRight,
  X,
  Printer,
  Copy,
  Server,
  WifiOff,
  RefreshCw,
  LayoutDashboard
} from 'lucide-react';
import { APPROVED_CLAIMS } from '../config/claimsConfig';
import { PRICING_CONFIG } from '../config/pricingConfig';
import { apiService } from '../services/api';
import { ApplicationData } from '../types';

const VALID_GUARDIAN_CODES = [
  "AG100101", "AG100102", "AG100103", "AG100104", 
  "AG100105", "AG100106", "AG100107", "AG100108"
];

const steps = [
  { id: 1, title: 'Business', icon: Building2, subtitle: 'Entity Info' },
  { id: 2, title: 'Owner', icon: User, subtitle: 'Personal Details' },
  { id: 3, title: 'Financials', icon: CreditCard, subtitle: 'Cash Flow' },
  { id: 4, title: 'KYC', icon: Camera, subtitle: 'Document Prep' },
  { id: 5, title: 'Finalize', icon: ShieldCheck, subtitle: 'Review & Consent' },
];

const ApplicationWizard: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0); 
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refId, setRefId] = useState<string | null>(null);
  const [activeTierIndex, setActiveTierIndex] = useState(0);
  const [locationCoords, setLocationCoords] = useState<{lat: number, lng: number} | null>(null);
  const [isAgentReferred, setIsAgentReferred] = useState<boolean | null>(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [transmissionStep, setTransmissionStep] = useState(0);
  const [isCopied, setIsCopied] = useState(false);

  const isLive = apiService.status.isLive();

  const [formData, setFormData] = useState({
    businessName: '',
    businessType: 'Retail',
    agentCode: '',
    fullName: '',
    idNumber: '',
    email: '',
    phone: '',
    address: '',
    dailySales: '',
    currentDebts: '',
    consents: {
      terms: false,
      privacy: false,
      dataUse: false
    }
  });

  const selectedTier = PRICING_CONFIG.PRICING_TIERS[activeTierIndex];
  const isValidAgentCode = VALID_GUARDIAN_CODES.includes(formData.agentCode);
  const showValidationError = isAgentReferred === true && formData.agentCode.length >= 8 && !isValidAgentCode;

  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        setLocationCoords({ lat: position.coords.latitude, lng: position.coords.longitude });
      }, (err) => console.warn("Location access denied."));
    }
  }, []);

  useEffect(() => {
    if (currentStep === 6) {
      const timer1 = setTimeout(() => setTransmissionStep(1), 800);
      const timer2 = setTimeout(() => setTransmissionStep(2), 2000);
      const timer3 = setTimeout(() => setTransmissionStep(3), 3500);
      return () => { clearTimeout(timer1); clearTimeout(timer2); clearTimeout(timer3); };
    }
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep === 1) {
      if (isAgentReferred === null) return alert("Select referral source.");
      if (isAgentReferred === true && !isValidAgentCode) return alert("Valid Agent Code required.");
      if (!formData.businessName.trim()) return alert("Business Name required.");
    }
    if (currentStep === 2) {
      if (!formData.fullName.trim() || !formData.idNumber.trim()) return alert("Complete identity fields.");
    }
    if (currentStep === 5) {
      if (!formData.consents.terms || !formData.consents.privacy || !formData.consents.dataUse) return alert("Accept all consents.");
      handleSubmit();
      return;
    }
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
    else setCurrentStep(0);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Let go of the uplink for now - proceed directly to successful intake state
    try {
      // Simulate a brief secure processing delay
      await new Promise(resolve => setTimeout(resolve, 1800));
      
      const generatedId = `CHUI-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
      setRefId(generatedId);
      setCurrentStep(6);
      window.scrollTo(0, 0);
    } catch (error) {
      console.error("Submission processing error", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyRef = () => {
    if (refId) {
      navigator.clipboard.writeText(refId);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="max-w-2xl mx-auto py-12 text-center animate-in fade-in duration-700">
            <div className="w-20 h-20 bg-chui-navy/5 rounded-[2rem] flex items-center justify-center mx-auto mb-10 text-chui-navy">
              <ShieldCheck size={40} />
            </div>
            <h2 className="text-4xl font-black text-slate-900 mb-6 tracking-tight uppercase">Capital Partnership Intake</h2>
            <p className="text-lg text-slate-600 font-bold mb-12 leading-relaxed">
              Start your journey to character-based credit. Your data is prepared for the Directorate verification via a secure digital record.
            </p>
            <button onClick={() => setCurrentStep(1)} className="bg-chui-navy text-white px-12 py-5 rounded-2xl font-black text-lg hover:bg-slate-800 transition-all shadow-2xl active:scale-95 uppercase tracking-widest flex items-center mx-auto">
              Begin Intake <ArrowRight size={20} className="ml-4" />
            </button>
          </div>
        );
      case 1:
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
            <h3 className="text-3xl font-black text-chui-navy mb-8 tracking-tight uppercase">1. Business Profile</h3>
            <div className="space-y-10">
              <div className="p-10 bg-slate-50 border border-slate-200 rounded-[3rem] shadow-sm">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 bg-chui-navy/10 rounded-xl flex items-center justify-center text-chui-navy"><HelpCircle size={20} /></div>
                  <div>
                    <label className="text-xs font-black text-slate-800 uppercase tracking-widest block">Attribution Checkpoint</label>
                    <p className="text-[10px] font-bold text-slate-500 uppercase">Were you referred to this portal by a Field Guardian?</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <button onClick={() => setIsAgentReferred(true)} className={`py-6 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 transition-all ${isAgentReferred === true ? 'bg-chui-navy border-chui-navy text-white shadow-xl scale-[1.02]' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>Yes, Agent Referred</button>
                  <button onClick={() => { setIsAgentReferred(false); setFormData({...formData, agentCode: ''}); }} className={`py-6 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 transition-all ${isAgentReferred === false ? 'bg-chui-navy border-chui-navy text-white shadow-xl scale-[1.02]' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>No, Applying Directly</button>
                </div>
                {isAgentReferred && (
                  <div className="mt-10 pt-8 border-t border-slate-200 animate-in slide-in-from-top-4 duration-300">
                    <div className="relative">
                      <Key size={18} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input type="text" value={formData.agentCode} onChange={e => setFormData({...formData, agentCode: e.target.value.toUpperCase().trim()})} className={`w-full pl-14 pr-6 py-6 bg-white border-2 rounded-2xl font-black text-chui-navy uppercase tracking-[0.2em] outline-none transition-all text-xl ${isValidAgentCode ? 'border-success ring-4 ring-success/5' : 'border-slate-200'}`} placeholder="GUARDIAN ID" />
                    </div>
                  </div>
                )}
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-black text-slate-800 uppercase tracking-widest mb-3">Business Name</label>
                  <input type="text" value={formData.businessName} onChange={e => setFormData({...formData, businessName: e.target.value})} className="w-full p-6 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:bg-white focus:border-chui-navy outline-none text-lg" placeholder="e.g. Uzuri Boutique" />
                </div>
                <div className="relative">
                   <label className="block text-[11px] font-black text-slate-800 uppercase tracking-widest mb-3">Sector</label>
                   <select value={formData.businessType} onChange={e => setFormData({...formData, businessType: e.target.value})} className="w-full p-6 bg-slate-50 border border-slate-200 rounded-2xl font-bold focus:bg-white focus:border-chui-navy outline-none appearance-none">
                     <option>Retail</option>
                     <option>Hospitality</option>
                     <option>Services</option>
                   </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-black text-slate-800 uppercase tracking-widest mb-3">Growth Tier</label>
                <div className="grid grid-cols-1 gap-4">
                  {PRICING_CONFIG.PRICING_TIERS.map((t, idx) => (
                    <button key={t.no} onClick={() => setActiveTierIndex(idx)} className={`p-8 rounded-[2.5rem] border-2 text-left transition-all flex items-center justify-between ${activeTierIndex === idx ? 'border-chui-navy bg-chui-navy/5 shadow-lg' : 'border-slate-100 bg-white'}`}>
                      <div>
                        <p className="text-[10px] font-black uppercase text-slate-400">Tier {t.no}: {t.name}</p>
                        <p className="font-black text-slate-900 tracking-tight text-xl">KES {t.minSales.toLocaleString()}+ <span className="text-sm text-slate-400 font-bold">Daily Sales</span></p>
                      </div>
                      <div className="text-right">
                        <p className={`font-black tracking-tighter text-2xl ${activeTierIndex === idx ? 'text-chui-navy' : 'text-slate-900'}`}>KES {t.totalLimit.toLocaleString()}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
            <h3 className="text-3xl font-black text-chui-navy mb-8 tracking-tight uppercase">2. Identity Details</h3>
            <div className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="md:col-span-2">
                  <label className="block text-[11px] font-black text-slate-800 uppercase tracking-widest mb-3">Full Legal Name</label>
                  <input type="text" placeholder="Full Name" className="w-full p-6 bg-slate-50 border border-slate-200 rounded-2xl font-bold" value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} />
                </div>
                <div>
                  <label className="block text-[11px] font-black text-slate-800 uppercase tracking-widest mb-3">National ID</label>
                  <input type="text" placeholder="ID Number" className="w-full p-6 bg-slate-50 border border-slate-200 rounded-2xl font-bold" value={formData.idNumber} onChange={e => setFormData({...formData, idNumber: e.target.value})} />
                </div>
                <div>
                  <label className="block text-[11px] font-black text-slate-800 uppercase tracking-widest mb-3">Mobile Number</label>
                  <input type="tel" placeholder="07XX XXX XXX" className="w-full p-6 bg-slate-50 border border-slate-200 rounded-2xl font-bold" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                </div>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
            <h3 className="text-3xl font-black text-chui-navy mb-8 tracking-tight uppercase">3. Financial Health</h3>
            <div className="space-y-8">
                <div className="bg-[#1e2335] p-8 rounded-[2.5rem] text-white flex items-center gap-6">
                   <Zap size={32} className="text-chui-yellow" />
                   <p className="text-sm font-bold leading-relaxed">Your financial stewardship record is key. We evaluate cash-flow velocity to determine capital fit.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="md:col-span-2">
                    <label className="block text-[11px] font-black text-slate-800 uppercase tracking-widest mb-3">Email Address</label>
                    <input type="email" placeholder="owner@business.com" className="w-full p-6 bg-slate-50 border border-slate-200 rounded-2xl font-bold" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-black text-slate-800 uppercase tracking-widest mb-3">Monthly Debts</label>
                    <input type="number" className="w-full p-6 bg-slate-50 border border-slate-200 rounded-2xl font-bold" placeholder="0" value={formData.currentDebts} onChange={e => setFormData({...formData, currentDebts: e.target.value})} />
                  </div>
                </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
            <h3 className="text-3xl font-black text-chui-navy mb-8 tracking-tight uppercase">4. Field Verification Prep</h3>
            <p className="text-slate-600 font-bold mb-8">Ensure you have the following documents ready for your Guardian visit:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {['National ID', 'Business Permit', 'M-Pesa Statement', 'KRA PIN'].map((item, i) => (
                <div key={i} className="p-8 bg-white border-2 border-slate-100 rounded-[2.5rem] flex items-center gap-6 group hover:border-chui-navy/20 transition-all">
                  <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-chui-navy group-hover:bg-chui-navy group-hover:text-white transition-all"><FileText size={24} /></div>
                  <p className="text-[14px] font-black uppercase tracking-tight text-slate-900">{item}</p>
                </div>
              ))}
            </div>
          </div>
        );
      case 5:
        return (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 text-left">
            <h3 className="text-3xl font-black text-chui-navy mb-8 tracking-tight uppercase">5. Review & Authorize</h3>
            <div className="space-y-8">
              <div className="bg-[#1e2335] p-10 rounded-[3rem] text-white shadow-2xl relative overflow-hidden">
                <div className="space-y-6 relative z-10">
                  <div className="flex justify-between items-center"><span className="opacity-40 text-[10px] font-black uppercase tracking-widest">Business</span><span className="font-black uppercase">{formData.businessName || 'TBD'}</span></div>
                  <div className="flex justify-between items-center"><span className="opacity-40 text-[10px] font-black uppercase tracking-widest">Initial Limit</span><span className="text-chui-yellow font-black">KES {selectedTier.totalLimit.toLocaleString()}</span></div>
                  <div className="flex justify-between items-center"><span className="opacity-40 text-[10px] font-black uppercase tracking-widest">Attribution</span><span className="font-mono text-chui-yellow">{formData.agentCode || 'Direct-Intake'}</span></div>
                </div>
              </div>
              <div className="space-y-4">
                {['I accept Partnership Terms', 'I accept Privacy Notice', 'I authorize verification visit'].map((label, i) => (
                  <label key={i} className="flex items-center space-x-5 cursor-pointer p-6 bg-slate-50 rounded-[2rem] border border-slate-100 hover:bg-slate-100 transition-colors">
                    <input type="checkbox" className="w-6 h-6 accent-chui-navy" checked={i === 0 ? formData.consents.terms : i === 1 ? formData.consents.privacy : formData.consents.dataUse} onChange={() => {
                      const key = i === 0 ? 'terms' : i === 1 ? 'privacy' : 'dataUse';
                      setFormData({...formData, consents: {...formData.consents, [key]: !((formData.consents as any)[key])}});
                    }} />
                    <span className="text-[11px] font-black uppercase tracking-widest text-slate-800">{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );
      case 6:
        return (
          <div className="max-w-5xl mx-auto py-12 text-center animate-in zoom-in duration-700">
            <div className="mb-12">
               <div className="inline-flex items-center px-4 py-2 bg-amber-50 border border-amber-100 rounded-full mb-8 text-amber-700 gap-2">
                  <ShieldCheck size={12} />
                  <span className="text-[10px] font-black uppercase tracking-widest">
                    Secure Digital Intake Record Active
                  </span>
               </div>
               <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-10 border-4 border-white shadow-xl shadow-green-100">
                 <CheckCircle className="text-success" size={48} />
               </div>
               <h2 className="text-6xl md:text-7xl font-black text-chui-navy mb-8 tracking-tighter uppercase leading-[0.9]">Intake Successful!</h2>
               <p className="text-xl text-slate-800 mb-10 max-w-3xl mx-auto font-bold leading-relaxed px-6">{APPROVED_CLAIMS.INTAKE_SUCCESS_MSG}</p>
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-stretch mb-20 text-left px-4">
               <div onClick={() => setShowReceipt(true)} className="bg-white p-14 md:p-16 rounded-[4rem] shadow-2xl border-2 border-slate-50 flex flex-col items-center justify-center cursor-pointer hover:border-chui-navy/20 transition-all relative overflow-hidden group">
                  <div className="absolute top-0 right-0 p-8 opacity-[0.02] group-hover:scale-110 transition-transform"><ShieldCheck size={150} /></div>
                  <div className="flex items-center gap-2 mb-8 bg-chui-navy/5 px-6 py-3 rounded-full border border-chui-navy/10"><Lock size={14} className="text-chui-navy" /><p className="text-[11px] font-black text-chui-navy uppercase tracking-[0.2em]">Secure Ref ID</p></div>
                  <p className="text-5xl md:text-6xl font-black text-slate-900 tracking-tighter mb-6 font-mono uppercase">{refId}</p>
                  <button onClick={(e) => { e.stopPropagation(); handleCopyRef(); }} className="text-[10px] font-black text-slate-400 hover:text-chui-navy transition-colors uppercase flex items-center gap-2">
                    {isCopied ? 'Copied!' : <><Copy size={12} /> Copy Reference</>}
                  </button>
               </div>
               <div className="bg-[#1e2335] p-14 md:p-16 rounded-[4rem] text-white flex flex-col justify-center relative overflow-hidden shadow-2xl">
                  <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none"><Zap size={240} className="text-chui-yellow" /></div>
                  <h4 className="text-[12px] font-black uppercase tracking-[0.5em] text-chui-yellow mb-12">Verification Protocol</h4>
                  <div className="space-y-10">
                     <div className="flex items-center gap-8"><div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center text-chui-yellow border border-white/5"><UserPlus size={26} /></div><p className="text-base font-black uppercase tracking-tight">Record Queue Distribution Active</p></div>
                     <div className="flex items-center gap-8"><div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center text-chui-yellow border border-white/5"><PhoneCall size={26} /></div><p className="text-base font-black uppercase tracking-tight">Guardian Match Readiness</p></div>
                  </div>
               </div>
            </div>
            <div className="flex flex-col md:flex-row justify-center items-center gap-6 px-6">
              <Link to="/" className="w-full md:w-auto bg-[#1e2335] text-white px-16 py-7 rounded-3xl font-black text-sm uppercase tracking-[0.2em] shadow-2xl active:scale-95">Return to Dashboard</Link>
              <button onClick={() => setShowReceipt(true)} className="w-full md:w-auto bg-white text-slate-500 border-2 border-slate-100 px-16 py-7 rounded-3xl font-black text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-3 active:scale-95"><Download size={18} /> View Digital Receipt</button>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const isNextDisabled = currentStep === 1 && (isAgentReferred === null || (isAgentReferred === true && !isValidAgentCode));

  return (
    <div className="bg-[#f8fafc] min-h-screen py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {currentStep > 0 && currentStep < 6 && (
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 sticky top-32">
              <div className="bg-white p-12 rounded-[3.5rem] border border-slate-200 shadow-sm text-left relative overflow-hidden">
                <div className="space-y-12 relative z-10">
                  {steps.map((step) => (
                    <div key={step.id} className="flex items-center">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mr-6 border-2 transition-all ${currentStep === step.id ? 'bg-chui-navy border-chui-navy text-chui-yellow shadow-xl' : currentStep > step.id ? 'bg-green-100 border-green-100 text-success' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                        {currentStep > step.id ? <Check size={20} strokeWidth={4} /> : <step.icon size={20} />}
                      </div>
                      <div className="text-left">
                        <p className={`text-[11px] font-black uppercase tracking-[0.2em] mb-1 ${currentStep >= step.id ? 'text-slate-900' : 'text-slate-400'}`}>{step.title}</p>
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{step.subtitle}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-8">
              <div className="bg-white p-12 lg:p-20 rounded-[4rem] border border-slate-200 shadow-sm min-h-[650px] flex flex-col relative overflow-hidden">
                <div className="flex-grow">{renderStepContent()}</div>
                <div className="mt-20 pt-12 border-t border-slate-100 flex justify-between items-center">
                  <button onClick={handleBack} className="text-slate-400 font-black text-[11px] uppercase tracking-widest flex items-center hover:text-chui-navy transition-colors"><ArrowLeft size={16} className="mr-3" /> Step {currentStep - 1}</button>
                  <button onClick={handleNext} disabled={isSubmitting || isNextDisabled} className="bg-chui-navy text-white px-12 py-6 rounded-2xl font-black text-lg shadow-2xl uppercase tracking-widest disabled:opacity-50 active:scale-95 transition-all flex items-center">
                    {isSubmitting ? <Loader2 className="animate-spin" size={24} /> : (currentStep === 5 ? 'Authorize Intake' : 'Continue')} <ArrowRight size={20} className="ml-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {(currentStep === 0 || currentStep === 6) && renderStepContent()}
        {showReceipt && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in">
             <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl overflow-hidden p-10 md:p-14 relative text-left">
                <button onClick={() => setShowReceipt(false)} className="absolute top-8 right-8 text-slate-400 hover:text-slate-900 transition-colors p-2"><X size={24} /></button>
                <div className="mb-10 pb-10 border-b border-slate-100 flex justify-between items-start">
                   <div>
                      <h3 className="text-3xl font-black text-chui-navy uppercase tracking-tighter">Digital Record</h3>
                      <p className="text-sm font-bold text-slate-500 mt-2">OFFICIAL INTAKE SUMMARY • Ref: {refId}</p>
                   </div>
                   <ShieldCheck className="text-chui-navy" size={40} />
                </div>
                <div className="space-y-8 mb-12">
                   <div className="flex justify-between items-end border-b border-slate-50 pb-4">
                      <span className="text-xs font-black uppercase text-slate-400 tracking-widest">Registered Entity</span>
                      <span className="font-black text-slate-900 uppercase">{formData.businessName || 'DEMO BUSINESS'}</span>
                   </div>
                   <div className="flex justify-between items-end border-b border-slate-50 pb-4">
                      <span className="text-xs font-black uppercase text-slate-400 tracking-widest">Growth Tier</span>
                      <span className="font-black text-slate-900 uppercase">{selectedTier.name}</span>
                   </div>
                   <div className="flex justify-between items-end border-b border-slate-50 pb-4">
                      <span className="text-xs font-black uppercase text-slate-400 tracking-widest">Capital Limit</span>
                      <span className="font-black text-chui-navy uppercase">KES {selectedTier.totalLimit.toLocaleString()}</span>
                   </div>
                   <div className="flex justify-between items-end border-b border-slate-50 pb-4">
                      <span className="text-xs font-black uppercase text-slate-400 tracking-widest">Intake Date</span>
                      <span className="font-black text-slate-900 uppercase">{new Date().toLocaleDateString('en-KE', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                   </div>
                </div>
                <div className="flex gap-4">
                   <button onClick={() => window.print()} className="flex-grow bg-[#1e2335] text-white py-5 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 shadow-xl active:scale-95"><Printer size={18} /> Print Official PDF</button>
                </div>
             </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationWizard;